import React, { useState } from 'react';
import { graphql, useFragment } from 'react-relay';
import DropdownMenu, {
	DropdownItemGroup,
	type CustomTriggerProps,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import { useNavigationProject } from '@atlassian/jira-business-entity-project/src/services/navigation-project/index.tsx';
import { useProjectConnectItems } from '@atlassian/jira-navigation-apps-sidebar-connect/src/controllers/project/index.tsx';
import { useProjectForgeApps } from '@atlassian/jira-navigation-apps-sidebar-forge/src/controllers/project/index.tsx';
import Tab from '@atlassian/jira-navigation-kit-tab-old/src/ui/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { JiraNavigationItemTypeKey } from '@atlassian/jira-relay/src/__generated__/simpleNavigationTab_businessTabs.graphql';
import type { appNavigationTab_businessTabs$key } from '@atlassian/jira-relay/src/__generated__/appNavigationTab_businessTabs.graphql';
import { TabIcon } from '../../../common/ui/tab-icon/index.tsx';
import ConnectItem from './connect-item/index.tsx';
import ForgeItem from './forge-item/index.tsx';

type Props = {
	tabFragment: appNavigationTab_businessTabs$key;
	id: string;
	position: number;
	canEdit?: boolean;
	onTabClick: (itemTypeKey: JiraNavigationItemTypeKey, extra: { childCount: number }) => void;
	handleDrag?: () => void;
	beforeItemId: string | null;
	afterItemId: string | null;
};

/**
 * @deprecated delete when cleaning up getWillShowNav4
 */
export const AppNavigationTab = ({
	tabFragment,
	position,
	id,
	canEdit,
	onTabClick,
	handleDrag,
	beforeItemId,
	afterItemId,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const { project } = useNavigationProject();

	const { data: forgeAppsData, loading: forgeAppsLoading } =
		useProjectForgeApps('forge-spa-project');
	const { data: connectAppsData, loading: connectAppsLoading } = useProjectConnectItems();
	const forgeItems = forgeAppsData ?? [];
	const connectItems = connectAppsData ?? [];

	const totalNumberOfApps = forgeItems.length + connectItems.length;
	const hasApps = totalNumberOfApps > 0;

	const hasForgeApps = !forgeAppsLoading && forgeItems.length > 0;
	const hasConnectApps = !connectAppsLoading && connectItems.length > 0;
	const shouldRenderApps = project != null && (hasForgeApps || hasConnectApps);

	const handleItemClick = (appName: string, appType: 'connect' | 'forge') => {
		setIsDropdownOpen(false);
		fireUIAnalytics(createAnalyticsEvent({}), 'link clicked', 'app', {
			appName,
			appType,
		});
	};

	const tabData = useFragment(
		graphql`
			fragment appNavigationTab_businessTabs on JiraNavigationItem {
				...ui_navigationKitTabOld_Tab
			}
		`,
		tabFragment,
	);

	const trigger = ({ isSelected, onClick, triggerRef, ...triggerProps }: CustomTriggerProps) => (
		<Tab
			tabFragment={tabData}
			type="dropdown-trigger"
			isSelected={isSelected}
			elemBefore={<TabIcon typeKey="APPS" label="" size="small" />}
			onClick={(event) => {
				onClick && onClick(event);
				onTabClick('APPS', { childCount: totalNumberOfApps });
			}}
			position={position}
			id={id}
			ref={triggerRef}
			handleDrag={handleDrag}
			beforeItemId={beforeItemId}
			afterItemId={afterItemId}
			canEdit={canEdit}
			{...triggerProps}
		/>
	);

	if (!shouldRenderApps) {
		return null;
	}

	return (
		<DropdownMenu
			trigger={trigger}
			placement="bottom-end"
			isOpen={isDropdownOpen}
			onOpenChange={({ isOpen }: OnOpenChangeArgs) => {
				setIsDropdownOpen(() => isOpen);
			}}
		>
			{hasApps && (
				<DropdownItemGroup testId="business-tabs.ui.simple-navigation-tab.app-navigation-tab.dropdown-item-group">
					{forgeItems.map((forgeItem) => (
						<ForgeItem
							key={forgeItem.id}
							data={forgeItem}
							project={project}
							onItemClick={() => handleItemClick(forgeItem.properties.title, 'forge')}
						/>
					))}
					{connectItems.map((connectItem) => (
						<ConnectItem
							key={connectItem.id}
							data={connectItem}
							onItemClick={() => handleItemClick(connectItem.name, 'connect')}
						/>
					))}
				</DropdownItemGroup>
			)}
		</DropdownMenu>
	);
};
